// ************************************************************************************************************************************************************
//
// CONFIDENTIAL
// Unpublished Work, Copyright 2023, Baxter Healthcare Corporation. All rights reserved.
// For use only by Baxter Healthcare Corporation.
// This document contains proprietary information. It must not be reproduced
// or disclosed to others without prior written approval.
//
// Filename: AddMonitor.js
// Description: This file is responsible to Add monitors.
//
// ************************************************************************************************************************************************************
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect , useCallback, useRef  } from "react";
import { makeStyles } from "@material-ui/core";
import {
  TextField,
  FormControl,
} from "@material-ui/core";
import { useFormik } from "formik";
import * as Yup from "yup";
import { addMonitor, editMonitor, getOneMonitor } from "../_redux/monitorCrud";
import { getAllFacilityNames } from "../../Facilities/_redux/facilityCrud";
import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  InputAdornment,
} from "@material-ui/core";
import HighlightOffRoundedIcon from "@material-ui/icons/HighlightOffRounded";
import CloseIcon from "@material-ui/icons/CloseOutlined";
import { Slide } from "@material-ui/core";
import AsyncSelect from 'react-select/async';
import debounce from "lodash/debounce";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#158380"
    }
  },
  alertMsg: {
    color: "red"
  },
  MuiBackdrop: {
    backgroundColor: "rgba(0,0,0,0.6)"
  }
}));

let initialValues = {
  cloud_id: "",
  monitor_id: "",
  facility: "",
  auto_log_level: "500"
};

export default function AddMonitorPage(props) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [responseStatus, setResponse] = useState(false);
  const [errorMsg, setMessage] = useState("");
  const [selectedFacilityId, setSelectedFacilityId] = useState("");
  const [cloudId, setCloudId] = useState("");
  const [monitorId, setMonitorId] = useState("");
  const [autoLogLevel, setautoLogLevel] = useState("500");
  const [uiLoading, setUiLoading] = useState(false);
  const [monitors, setMonitors] = useState(null);
  const [, setFacilities] = useState(null);
  const selectfacilityRef = useRef(null);
  const FacilitySchema = Yup.object().shape({
    cloud_id: Yup.string()
      .trim()
      .required("Monitor Serial Number is required"),
    monitor_id: Yup.string()
      .trim()
      .required("Monitor Name is required")
      .max(15, "15 Characters allowed"),
    auto_log_level: Yup.string()
      .trim()
      .required("Auto Log Level is required")
  });

  const enableLoading = () => {
    setLoading(true);
  };
  const disableLoading = () => {
    setLoading(false);
  };
  const disableMessage = () => {
    setResponse(false);
  };
  const enableMessage = () => {
    setResponse(true);
  };

  useEffect(() => {
    checkAddOrEdit();
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [props.is_edit]);

  const checkAddOrEdit = () => {
    if (props.is_edit === "true" && props.edit_id) {
      setUiLoading(true);
      getOneMonitor(props.edit_id).then((res) => {
        setUiLoading(false);
        if (res.data.status) {
          let data = res.data.data[0];
          setMonitors(data);
          setUiLoading(false);
          setCloudId(data.cloud_id);
          initialValues.cloud_id = data.cloud_id;
          if(data.monitor_id === null){
            setMonitorId("");
          }
          else{
            setMonitorId(data.monitor_id);
            initialValues.monitor_id = data.monitor_id;
          }
          setautoLogLevel(data.auto_log_level);
          initialValues.auto_log_level = data.auto_log_level;
          if (
            data.assigned_facility !== undefined &&
            data.assigned_facility.id !== null
          ) {
            setFacilities(data.assigned_facility);
            setSelectedFacilityId(data.assigned_facility);
          } else if( data.assigned_facility.id === null) {
            setFacilities("");
            setSelectedFacilityId("");
          }
          else{
            setFacilities("");
            setSelectedFacilityId("");
          }
        }
        else {
          enableMessage();
          setMessage(res.data.message);
        }
      })
      .catch((error) => {
        disableLoading();
        enableMessage();
        if (error.response) {
          if (error.response.status !== 404 && error) {
            setMessage(error.response.data.message);
          } else {
            props.history.push("error/error-v1");
          }
        } else {
          setMessage(error.message);
        }
      });
      formik.handleChange("cloud_id");
    } else {
      setUiLoading(false);
    }
  };

  const handleChangeLocation = (selectedFacilityId)=> {
    if (selectedFacilityId !== undefined && selectedFacilityId === null){
      setSelectedFacilityId("");
    }
    else{
      setSelectedFacilityId(selectedFacilityId);
    }
  }
  const handleInputChange = (value) =>{
    setFacilities(value);    
  }
  const clearfacility = () =>{
    selectfacilityRef.current.clearValue();
    setSelectedFacilityId("");
  }

  const cloudIdValidation = (e) => {
    e.target.value = e.target.value.replace(/[^A-Za-z0-9-_]/g, "");
    setCloudId(e.target.value);
  };

  const monitorIdValidation = (e) => {
    e.target.value = e.target.value.replace(/[^A-Za-z0-9\s]/g, "");
    setMonitorId(e.target.value);
  };

  const setLogLevel = (e) => {
    e.target.value = e.target.value.replace(/[^\d]+/g, "");
    setautoLogLevel(e.target.value);
  };

  const cancel = (opt) => {
    setCloudId("");
    setMonitorId("");
    setResponse(false);
    setautoLogLevel("500");    
    props.onHide("cancel");
  };

  const addCancel = () => {
    setTimeout(() => {
      setSelectedFacilityId("");
      setCloudId("");
      setMonitorId("");
      setautoLogLevel("500");
    }, 2000);
    props.onHide("add");
  };

  const formik = useFormik({
    initialValues,
    validationSchema: FacilitySchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      enableLoading();
      disableMessage();
      if (props.is_edit === "true") {
       
        let id = monitors.id;
        editMonitor(monitorId,  selectedFacilityId.id, id, autoLogLevel)
          .then((res) => {
            setSubmitting(false);
            disableLoading();
            if (res.data.status) {
              addCancel();
            } else {
              enableMessage();
              setMessage(res.data.message);
            }
          })
          .catch((error) => {
            disableLoading();
            setSubmitting(false);
            enableMessage();
            if (error.response) {
              if (error.response.status !== 404 && error) {
                setMessage(error.response.data.message);
              } else {
                props.history.push("error/error-v1");
              }
            } else {
              setMessage(error.message);
            }
          });
      } else {
        addMonitor(
          cloudId,
          monitorId,          
          selectedFacilityId.id,
          autoLogLevel
        )
          .then((res) => {
            setSubmitting(false);
            disableLoading();
            if (res.data.status) {
              addCancel();
            } else {
              enableMessage();
              setMessage(res.data.message);
            }
          })
          .catch((error) => {
            disableLoading();
            setSubmitting(false);
            enableMessage();
            if (error.response) {
              if (error.response.status !== 404 && error) {
                setMessage(error.response.data.message);
              } else {
                props.history.push("error/error-v1");
              }
            } else {
              setMessage(error.message);
            }
          });
      }
    }
  });

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const loadFacilityDebounced = useCallback(
    debounce((facilities, callback) => {
      getAllFacilityNames(facilities).then(res => callback(res.data.data)).catch((error) => {});
    }, 500),
    []
  );

  return (
    <div>
      <Dialog
        PaperProps={{}}
        className={classes.MuiBackdrop + " popupstyle"}
        disableBackdropClick
        TransitionComponent={Transition}
        keepMounted
        fullWidth={true}
        maxWidth="sm"
        open={props.show}
        onClose={cancel}
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        <div className="dialogTitle">
          <DialogTitle id="scroll-dialog-title">
            {props.is_edit === "true" ? (
              <span className="dialogtitle">Edit Monitor</span>
            ) : (
              <span className="dialogtitle">Add Monitor</span>
            )}
          </DialogTitle>

          <IconButton onClick={cancel} aria-label="close" className="closebtn">
            <CloseIcon />
            close
          </IconButton>
        </div>

        <DialogContent>
          <DialogContentText
            component="div"
            id="scroll-dialog-description"
            tabIndex={-1}
          >
            {!uiLoading ? (
              <form
                onSubmit={formik.handleSubmit}
                id="myMonitorForm"
                autoComplete="off"
              >
              
                <div className="row">
                  <div className="col-md-12">
                    <TextField
                      className={classes.root}
                      disabled={props.is_edit === "true" ? true : false}
                      name="cloud_id"
                      value={cloudId}
                      onInput={cloudIdValidation}
                      id="cloud_id"
                      margin="normal"
                      label="Monitor Serial Number"
                      placeholder="Monitor Serial Number"
                      type="text"
                      fullWidth
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      inputProps={{ maxLength: 50 }}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              onClick={() => {
                                setCloudId("");
                              }}
                              onMouseDown={handleMouseDownPassword}
                            >
                              <HighlightOffRoundedIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    {formik.touched.cloud_id && formik.errors.cloud_id ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.cloud_id}
                        </div>
                      </div>
                    ) : null}

                    <div className="row">
                      <div className="col">
                        <TextField
                          className={classes.root}
                          disabled={props.facility === "deleted" ? true : false}
                          name="monitor_id"
                          value={monitorId}
                          onInput={monitorIdValidation}
                          id="monitor_id"
                          margin="normal"
                          label="Monitor Name"
                          placeholder="Monitor Name"
                          type="text"
                          inputProps={{ maxLength: 15 }}
                          fullWidth
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={() => {
                                    setMonitorId("");
                                  }}
                                  onMouseDown={handleMouseDownPassword}
                                >
                                  <HighlightOffRoundedIcon />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />

                        {formik.touched.monitor_id &&
                        formik.errors.monitor_id ? (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">
                              {formik.errors.monitor_id}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <TextField
                          className={classes.root}
                          disabled={props.facility === "deleted" ? true : false}
                          type="number"
                          name="auto_log_level"
                          value={autoLogLevel}
                          onInput={setLogLevel}
                          style={{ width: "100%" }}
                          id="auto_log_level"
                          margin="normal"
                          label="Auto Log Level"
                          placeholder="Auto Log Level"
                          fullWidth
                          required
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: 500
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={() => {
                                    setautoLogLevel("");
                                  }}
                                  onMouseDown={handleMouseDownPassword}
                                >
                                  {" "}
                                  <HighlightOffRoundedIcon />{" "}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />

                        {formik.touched.auto_log_level &&
                        formik.errors.auto_log_level ? (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">
                              {formik.errors.auto_log_level}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="row" style={{ marginTop: "12px" }}>
                      <div className="col">
                        <FormControl
                          variant="outlined"
                          fullWidth
                          size="small"
                          className={classes.root}
                        >
                       <AsyncSelect  
                       id="facility"
                       name="facility" 
                       ref={selectfacilityRef}   
                       classNamePrefix="async-select" 
                       value ={selectedFacilityId !== null ? selectedFacilityId : ""}               
                       placeholder = "select Facility"             
                       getOptionLabel= {e=> e.name}
                       getOptionValue = {e => e.id}
                       loadOptions={loadFacilityDebounced}                     
                       onChange={handleChangeLocation}
                       onInputChange={handleInputChange}
                       onSelectResetsInput={true}
                       onBlurResetsInput={clearfacility}
                       isClearable = {clearfacility}
                       noOptionsMessage={() => 'No Records Found'}
                       />
                        </FormControl>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                {responseStatus ? (
                  <div style={{marginTop: "10px"}} className={`d-flex p-2 ${classes.alertMsg}`}>{errorMsg}</div>
                ) : (
                  ""
                )}
               </div>
                <DialogActions style={{paddingRight: "0"}}> 
                <button className="cancelButton btn btn shadow-btn " onClick={cancel}>
                  Cancel
                </button>
                {props.facility === "MONITOR_DELETED" && props.is_edit === "true" ? (
                  <Button
                    className="saveButton"
                    color="secondary"
                    variant="contained"
                    disabled
                    type="submit"
                    form="myMonitorForm"
                  >
                    save
                    {loading && <span className="ml-3 spinner spinner-light"></span>}
                  </Button>
                ) : (
                  <button
                    className="saveButton"
                    color="secondary"
                    variant="contained"
                    type="submit"
                    form="myMonitorForm"
                  >
                    Save
                    {loading && <span className="ml-3 spinner spinner-light"></span>}
                  </button>
                )}
                </DialogActions>
              </form>
            ) : (
              <div align="center">               
              {responseStatus ? (
                 <div style={{marginBottom: "30px"}} className={`d-flex p-2 ${classes.alertMsg}`}>
                   {errorMsg}
                 </div>
               ) : (
                <span className="ml-3 spinner spinner-dark"></span>
               )}
            </div>
            )}
          </DialogContentText>
         
        </DialogContent>
      
      </Dialog>
    </div>
  );
}
